import { Button, Skeleton } from 'antd'
import { Minus, Plus, RotateCw } from 'lucide-react'
import mermaid from 'mermaid'
import { useEffect, useRef, useState } from 'react'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'

import { useStyles } from '../styles'

import { MERMAID_ZOOM, nextMermaidId } from './config'

export const LoadingMermaid = () => {
  const { styles } = useStyles()

  return (
    <div className={styles.mermaidSkeleton}>
      <Skeleton.Image active style={{ width: '100%', height: 280 }} />
    </div>
  )
}

export const MermaidDiagram = ({ code }: { code: string }) => {
  const { styles } = useStyles()
  const hostRef = useRef<HTMLDivElement>(null)
  const transformRef = useRef<ReactZoomPanPinchRef>(null)
  const [unrenderableCode, setUnrenderableCode] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const id = nextMermaidId()

    const render = async () => {
      try {
        const valid = await mermaid.parse(code, { suppressErrors: true })

        if (!valid) throw new Error('Invalid mermaid syntax')

        const { svg } = await mermaid.render(id, code)

        if (cancelled || !hostRef.current) return

        hostRef.current.innerHTML = svg
        requestAnimationFrame(() => {
          const host = hostRef.current

          if (!cancelled && host) {
            transformRef.current?.zoomToElement(host, undefined, 0)
          }
        })
      } catch {
        if (!cancelled) setUnrenderableCode(code)
      }
    }

    render()

    return () => {
      cancelled = true
    }
  }, [code])

  const resetView = () => {
    const host = hostRef.current

    if (host) {
      transformRef.current?.zoomToElement(
        host,
        undefined,
        MERMAID_ZOOM.resetAnimationMs
      )
    }
  }

  if (unrenderableCode === code) {
    return <pre className={styles.toolPayload}>{code}</pre>
  }

  return (
    <div className={styles.mermaidViewer}>
      <TransformWrapper
        ref={transformRef}
        minScale={MERMAID_ZOOM.minScale}
        maxScale={MERMAID_ZOOM.maxScale}
        limitToBounds={false}
        centerOnInit
      >
        <TransformComponent
          wrapperClass={styles.mermaidTransformWrapper}
          contentClass={styles.mermaidTransformContent}
        >
          <div ref={hostRef} />
        </TransformComponent>
      </TransformWrapper>

      <div className={styles.mermaidZoomControls}>
        <Button
          size="small"
          type="text"
          icon={<Plus size={16} />}
          onClick={() => transformRef.current?.zoomIn(MERMAID_ZOOM.step)}
        />
        <Button
          size="small"
          type="text"
          icon={<Minus size={16} />}
          onClick={() => transformRef.current?.zoomOut(MERMAID_ZOOM.step)}
        />
        <Button
          size="small"
          type="text"
          icon={<RotateCw size={16} />}
          onClick={resetView}
        />
      </div>
    </div>
  )
}
